import * as THREE from 'three';
import { Earth } from '../entities/Earth';
import { OrbitMoon } from '../entities/OrbitMoon';
import type { SavedMoonDesign } from '../game/MoonDesign';

type OrbitRecord = {
  design: SavedMoonDesign;
  moon: OrbitMoon;
  plane: THREE.Group;
  pivot: THREE.Group;
  ring: THREE.LineLoop;
  radius: number;
  angle: number;
  speed: number;
};

export type OrbitSceneSnapshot = {
  active: boolean;
  moonCount: number;
  selectedMoonId: string | null;
  focusedMoonId: string | null;
  focusedRotation: { yaw: number; pitch: number } | null;
  selectedScreen: { x: number; y: number } | null;
  attaching: boolean;
  attachmentProgress: number;
};

const ATTACH_DURATION = 1.8;

export class MoonOrbitScene {
  readonly group = new THREE.Group();
  private readonly systemRoot = new THREE.Group();
  private readonly earth = new Earth();
  private readonly records: OrbitRecord[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerNdc = new THREE.Vector2();
  private active = false;
  private selectedMoonId: string | null = null;
  private attachmentId: string | null = null;
  private attachmentProgress = 1;
  private focusedMoonId: string | null = null;
  private focusProgress = 0;
  private pointerId: number | null = null;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private startYaw = 0;
  private startPitch = 0;
  private pointerMoved = false;
  private readonly startMoonQuaternion = new THREE.Quaternion();
  private readonly parentWorldQuaternion = new THREE.Quaternion();
  private readonly cameraWorldQuaternion = new THREE.Quaternion();
  private readonly screenUpInParent = new THREE.Vector3();
  private readonly screenRightInParent = new THREE.Vector3();
  private readonly yawDelta = new THREE.Quaternion();
  private readonly pitchDelta = new THREE.Quaternion();
  private readonly overviewCamera = new THREE.Vector3();
  private readonly focusCamera = new THREE.Vector3();
  private readonly focusTarget = new THREE.Vector3();
  private readonly cameraTarget = new THREE.Vector3();
  private readonly projectedPoint = new THREE.Vector3();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly canvas: HTMLCanvasElement,
  ) {
    this.systemRoot.add(this.earth.group);
    this.group.add(this.systemRoot);
    this.group.visible = false;
  }

  setActive(active: boolean): void {
    this.active = active;
    this.group.visible = active;
    if (!active) this.cancelPointer();
  }

  setMoons(designs: readonly SavedMoonDesign[], animateMoonId: string | null = null): void {
    this.clearMoons();
    designs.forEach((design, index) => this.addMoon(design, index));
    this.attachmentId = animateMoonId;
    this.attachmentProgress = animateMoonId ? 0 : 1;
    this.focusedMoonId = null;
    this.focusProgress = 0;
    this.selectedMoonId = animateMoonId ?? designs[0]?.id ?? null;
    this.updateSelection();
  }

  update(delta: number, reducedMotion: boolean): void {
    if (!this.active) return;
    this.earth.update(delta, reducedMotion);
    if (this.attachmentId) {
      this.attachmentProgress = Math.min(1, this.attachmentProgress + delta / (reducedMotion ? 0.18 : ATTACH_DURATION));
      if (this.attachmentProgress >= 1) this.attachmentId = null;
    }
    const focusGoal = this.focusedMoonId ? 1 : 0;
    this.focusProgress = THREE.MathUtils.lerp(
      this.focusProgress,
      focusGoal,
      1 - Math.exp(-delta * (reducedMotion ? 18 : 6.8)),
    );
    const focusedClose = this.focusProgress > 0.72;
    this.earth.group.visible = !focusedClose;

    for (const record of this.records) {
      const isAttaching = record.design.id === this.attachmentId;
      const isFocused = record.design.id === this.focusedMoonId;
      const eased = THREE.MathUtils.smootherstep(this.attachmentProgress, 0, 1);
      if (!isAttaching && !this.focusedMoonId) record.angle += delta * record.speed * (reducedMotion ? 0.32 : 1);
      record.pivot.rotation.z = record.angle;
      record.moon.group.position.x = record.radius * (isAttaching ? eased : 1);
      const orbitScale = isAttaching ? THREE.MathUtils.lerp(0.25, 1, eased) : 1;
      const focusScale = isFocused ? THREE.MathUtils.lerp(1, 1.8, this.focusProgress) : 1;
      record.moon.group.scale.setScalar(orbitScale * focusScale);
      record.moon.group.visible = !focusedClose || isFocused;
      record.ring.visible = !focusedClose;
      (record.ring.material as THREE.LineBasicMaterial).opacity = record.design.id === this.selectedMoonId
        ? 0.42
        : isAttaching
          ? eased * 0.32
          : 0.1;
      record.moon.update(delta, !isFocused);
    }
  }

  applyCamera(delta: number): void {
    if (!this.active) return;
    const maxRadius = this.records.reduce((value, record) => Math.max(value, record.radius), 3.5);
    const portrait = this.camera.aspect < 0.9;
    const distance = portrait ? Math.max(15.8, maxRadius * 2.75) : Math.max(11.8, maxRadius * 1.68);
    this.overviewCamera.set(0, portrait ? 0.15 : 0, distance);
    this.focusTarget.set(0, 0, 0);
    const focused = this.records.find((record) => record.design.id === this.focusedMoonId);
    if (focused) {
      this.group.updateWorldMatrix(true, true);
      focused.moon.group.getWorldPosition(this.focusTarget);
    }
    this.focusCamera.copy(this.focusTarget);
    this.focusCamera.z += portrait ? 5.8 : 3.15;
    const easedFocus = THREE.MathUtils.smootherstep(this.focusProgress, 0, 1);
    this.overviewCamera.lerp(this.focusCamera, easedFocus);
    this.camera.position.lerp(this.overviewCamera, 1 - Math.exp(-delta * 5.4));
    this.cameraTarget.lerp(this.focusTarget, 1 - Math.exp(-delta * 7.2));
    this.camera.lookAt(this.cameraTarget);
  }

  resetView(): void {
    this.focusedMoonId = null;
    this.systemRoot.rotation.set(-0.08, -0.18, 0);
    if (!this.records.some((record) => record.design.id === this.selectedMoonId)) {
      this.selectedMoonId = this.records[0]?.design.id ?? null;
    }
    this.updateSelection();
  }

  handlePointerDown(event: PointerEvent): boolean {
    if (!this.active || this.pointerId !== null) return false;
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.startYaw = this.systemRoot.rotation.y;
    this.startPitch = this.systemRoot.rotation.x;
    const focused = this.getFocusedRecord();
    if (focused) {
      focused.moon.group.updateWorldMatrix(true, false);
      this.startMoonQuaternion.copy(focused.moon.group.quaternion);
      focused.moon.group.parent?.getWorldQuaternion(this.parentWorldQuaternion);
      this.parentWorldQuaternion.invert();
      this.camera.getWorldQuaternion(this.cameraWorldQuaternion);
      this.screenUpInParent
        .set(0, 1, 0)
        .applyQuaternion(this.cameraWorldQuaternion)
        .applyQuaternion(this.parentWorldQuaternion)
        .normalize();
      this.screenRightInParent
        .set(1, 0, 0)
        .applyQuaternion(this.cameraWorldQuaternion)
        .applyQuaternion(this.parentWorldQuaternion)
        .normalize();
    }
    this.pointerMoved = false;
    this.canvas.setPointerCapture?.(event.pointerId);
    return true;
  }

  handlePointerMove(event: PointerEvent): boolean {
    if (!this.active || event.pointerId !== this.pointerId) return false;
    const dx = event.clientX - this.pointerStartX;
    const dy = event.clientY - this.pointerStartY;
    this.pointerMoved ||= Math.hypot(dx, dy) > 7;
    const focused = this.getFocusedRecord();
    if (focused) {
      this.yawDelta.setFromAxisAngle(this.screenUpInParent, dx * 0.009);
      this.pitchDelta.setFromAxisAngle(this.screenRightInParent, dy * 0.007);
      focused.moon.group.quaternion
        .copy(this.startMoonQuaternion)
        .premultiply(this.pitchDelta)
        .premultiply(this.yawDelta)
        .normalize();
      return true;
    }
    this.systemRoot.rotation.y = this.startYaw + dx * 0.006;
    this.systemRoot.rotation.x = THREE.MathUtils.clamp(this.startPitch + dy * 0.0045, -0.68, 0.68);
    return true;
  }

  handlePointerUp(event: PointerEvent): boolean {
    if (!this.active || event.pointerId !== this.pointerId) return false;
    if (!this.pointerMoved && !this.focusedMoonId) this.selectAt(event);
    this.cancelPointer();
    return true;
  }

  getSelectedDesign(): SavedMoonDesign | null {
    return this.records.find((record) => record.design.id === this.selectedMoonId)?.design ?? null;
  }

  getFocusedDesign(): SavedMoonDesign | null {
    return this.records.find((record) => record.design.id === this.focusedMoonId)?.design ?? null;
  }

  focusSelected(): boolean {
    if (!this.selectedMoonId || !this.records.some((record) => record.design.id === this.selectedMoonId)) return false;
    this.focusedMoonId = this.selectedMoonId;
    return true;
  }

  exitFocus(): void {
    this.focusedMoonId = null;
  }

  getSnapshot(): OrbitSceneSnapshot {
    const selected = this.records.find((record) => record.design.id === this.selectedMoonId);
    const focused = this.getFocusedRecord();
    let selectedScreen: { x: number; y: number } | null = null;
    if (selected && !this.focusedMoonId) {
      const projected = selected.moon.group.getWorldPosition(this.projectedPoint).project(this.camera);
      selectedScreen = { x: projected.x * 0.5 + 0.5, y: -projected.y * 0.5 + 0.5 };
    }
    return {
      active: this.active,
      moonCount: this.records.length,
      selectedMoonId: this.selectedMoonId,
      focusedMoonId: this.focusedMoonId,
      focusedRotation: focused
        ? { yaw: focused.moon.group.rotation.y, pitch: focused.moon.group.rotation.x }
        : null,
      selectedScreen,
      attaching: this.attachmentId !== null,
      attachmentProgress: this.attachmentProgress,
    };
  }

  dispose(): void {
    this.cancelPointer();
    this.clearMoons();
    this.earth.dispose();
  }

  private addMoon(design: SavedMoonDesign, index: number): void {
    const plane = new THREE.Group();
    const pivot = new THREE.Group();
    const radius = 3.45 + index * 0.72;
    const angle = ((index * 2.399963229728653) % (Math.PI * 2)) + 0.35;
    const moon = new OrbitMoon(design, index < 12 ? 1 : 0);
    const ringGeometry = new THREE.BufferGeometry();
    const points: THREE.Vector3[] = [];
    for (let segment = 0; segment < 96; segment += 1) {
      const t = segment / 96 * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(t) * radius, Math.sin(t) * radius, 0));
    }
    ringGeometry.setFromPoints(points);
    const ringMaterial = new THREE.LineBasicMaterial({
      color: '#9bcbd0',
      transparent: true,
      opacity: 0.1,
      depthWrite: false,
    });
    const ring = new THREE.LineLoop(ringGeometry, ringMaterial);
    plane.rotation.x = -0.23 + (index % 4) * 0.15;
    plane.rotation.y = ((index % 3) - 1) * 0.11;
    plane.rotation.z = index % 2 === 0 ? 0.08 : -0.11;
    pivot.rotation.z = angle;
    moon.group.position.x = radius;
    pivot.add(moon.group);
    plane.add(ring, pivot);
    this.systemRoot.add(plane);
    this.records.push({
      design,
      moon,
      plane,
      pivot,
      ring,
      radius,
      angle,
      speed: (index % 2 === 0 ? 1 : -1) * 0.19 / Math.sqrt(1 + index * 0.24),
    });
  }

  private clearMoons(): void {
    for (const record of this.records) {
      record.moon.dispose();
      record.ring.geometry.dispose();
      (record.ring.material as THREE.Material).dispose();
      this.systemRoot.remove(record.plane);
    }
    this.records.length = 0;
  }

  private selectAt(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerNdc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.camera.updateMatrixWorld();
    this.group.updateWorldMatrix(true, true);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const objects = this.records.map((record) => record.moon.group);
    const hit = this.raycaster.intersectObjects(objects, true)[0];
    const id = typeof hit?.object.userData.moonId === 'string' ? hit.object.userData.moonId : null;
    if (id) {
      this.selectedMoonId = id;
      this.focusedMoonId = id;
    }
    this.updateSelection();
  }

  private getFocusedRecord(): OrbitRecord | undefined {
    return this.records.find((record) => record.design.id === this.focusedMoonId);
  }

  private updateSelection(): void {
    for (const record of this.records) record.moon.setSelected(record.design.id === this.selectedMoonId);
  }

  private cancelPointer(): void {
    if (this.pointerId !== null && this.canvas.hasPointerCapture?.(this.pointerId)) {
      this.canvas.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;
    this.pointerMoved = false;
  }
}
